import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'
import {
  useDisableWebDevice,
  useEnableWebDevice,
  useRegenerateWebDeviceToken,
  useUpdateDevice,
  useAlarmRecipients,
  useAlarmRecipientCandidates,
  useSetAlarmRecipients,
} from '../../hooks/useDevices'
import { useTemplates } from '../../hooks/useTemplates'

// Fusos IANA mais usados (vazio = UTC). O device envia "ts" em hora local sem offset;
// este fuso diz ao servidor como interpretá-la para gravar UTC no InfluxDB.
const TIMEZONE_OPTIONS = [
  'America/Sao_Paulo',
  'America/Bahia',
  'America/Fortaleza',
  'America/Belem',
  'America/Cuiaba',
  'America/Manaus',
  'America/Rio_Branco',
  'America/Argentina/Buenos_Aires',
  'America/Montevideo',
  'America/Asuncion',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
]

export default function DeviceEditModal({
  device,
  isOpen,
  onClose,
  onTokenIssued,
  onAfterWebDeviceChange,
  onDevicePatch,
}) {
  const { t } = useTranslation()
  const updateDevice = useUpdateDevice()
  const enableWd = useEnableWebDevice()
  const regenWd = useRegenerateWebDeviceToken()
  const disableWd = useDisableWebDevice()

  const [name, setName] = useState('')
  const [nameError, setNameError] = useState(null)
  const [templateId, setTemplateId] = useState('')
  const [templateSaving, setTemplateSaving] = useState(false)
  const [timezone, setTimezone] = useState('')
  const [timezoneSaving, setTimezoneSaving] = useState(false)
  const [recipientIds, setRecipientIds] = useState(new Set())

  const appId = device ? (device.applicationId ?? device.application_id) : undefined
  const { data: templates } = useTemplates(appId)

  const { data: alarmRecipients } = useAlarmRecipients(isOpen && device ? device.id : undefined)
  const { data: alarmCandidates } = useAlarmRecipientCandidates()
  const setAlarmRecipients = useSetAlarmRecipients(device?.id)
  const [recipientsSaving, setRecipientsSaving] = useState(false)

  const devEui = device ? device.devEui ?? device.dev_eui ?? '' : ''
  const wdDisabled = !String(devEui).trim()
  const wdOn = device ? (device.webDeviceEnabled ?? device.web_device_enabled) : false

  useEffect(() => {
    if (!isOpen || !device) return
    setName(device.name ?? '')
    setNameError(null)
    setTemplateId(device.templateId ?? device.template_id ?? '')
    setTimezone(device.timezone ?? device.time_zone ?? '')
  }, [isOpen, device])

  useEffect(() => {
    if (Array.isArray(alarmRecipients)) {
      setRecipientIds(new Set(alarmRecipients.map((r) => r.tenantUserId ?? r.TenantUserId)))
    }
  }, [alarmRecipients])

  const currentTimezone = String(device?.timezone ?? device?.time_zone ?? '')

  const nameTrimmed = name.trim()
  const nameUnchanged = device && nameTrimmed === (device.name ?? '').trim()

  const saveName = async (e) => {
    e?.preventDefault()
    if (!nameTrimmed) {
      setNameError(t('devices.modalEdit.nameRequired'))
      return
    }
    if (!device?.id) return
    const prev = (device.name ?? '').trim()
    if (nameTrimmed === prev) {
      setNameError(null)
      return
    }
    try {
      const updated = await updateDevice.mutateAsync({
        id: device.id,
        payload: { name: nameTrimmed },
      })
      onDevicePatch?.(updated)
      setNameError(null)
      await onAfterWebDeviceChange?.()
    } catch (err) {
      alert(err.message || t('devices.modalEdit.saveNameError'))
    }
  }

  const runEnable = async () => {
    try {
      const res = await enableWd.mutateAsync(devEui)
      const tok = res.token ?? res.Token
      if (tok) onTokenIssued?.({ token: tok, message: res.message ?? res.Message })
      onDevicePatch?.({ webDeviceEnabled: true, web_device_enabled: true })
      await onAfterWebDeviceChange?.()
    } catch (e) {
      alert(e.message || t('devices.modalEdit.enableFailed'))
    }
  }

  const runRegen = async () => {
    if (!window.confirm(t('devices.modalEdit.regenConfirm'))) return
    try {
      const res = await regenWd.mutateAsync(devEui)
      const tok = res.token ?? res.Token
      if (tok) onTokenIssued?.({ token: tok, message: res.message ?? res.Message })
      await onAfterWebDeviceChange?.()
    } catch (e) {
      alert(e.message || t('devices.modalEdit.regenFailed'))
    }
  }

  const runDisable = async () => {
    if (!window.confirm(t('devices.modalEdit.disableConfirm'))) return
    try {
      await disableWd.mutateAsync(devEui)
      onDevicePatch?.({
        webDeviceEnabled: false,
        web_device_enabled: false,
        webDeviceTokenConfigured: false,
        web_device_token_configured: false,
      })
      await onAfterWebDeviceChange?.()
    } catch (e) {
      alert(e.message || t('devices.modalEdit.disableFailed'))
    }
  }

  if (!device) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('devices.modalEdit.title')}>
      <div className="space-y-5">
        <p className="text-xs text-gray-500 font-mono break-all">{devEui || '—'}</p>

        <form onSubmit={saveName} className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t('devices.modalEdit.nameLabel')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (nameError) setNameError(null)
            }}
            className={`input w-full ${nameError ? 'border-red-500' : ''}`}
            placeholder={t('devices.modalEdit.namePlaceholder')}
          />
          {nameError && <p className="text-sm text-red-600">{nameError}</p>}
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={updateDevice.isPending || !nameTrimmed || nameUnchanged}
          >
            {updateDevice.isPending ? t('devices.modalEdit.saveNameLoading') : t('devices.modalEdit.saveName')}
          </button>
        </form>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t('devices.modalEdit.templateLabel')}</label>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="input w-full"
          >
            <option value="">{t('devices.modalEdit.templateNone')}</option>
            {(templates ?? []).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500">
            {t('devices.modalEdit.templateHelp')}
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={
              templateSaving ||
              (templateId || '') === String(device?.templateId ?? device?.template_id ?? '')
            }
            onClick={async () => {
              if (!device?.id) return
              setTemplateSaving(true)
              try {
                const payload = templateId
                  ? { templateId }
                  : { clearTemplate: true }
                const updated = await updateDevice.mutateAsync({ id: device.id, payload })
                onDevicePatch?.(updated)
                await onAfterWebDeviceChange?.()
              } catch (err) {
                alert(err.message || t('devices.modalEdit.saveTemplateError'))
              } finally {
                setTemplateSaving(false)
              }
            }}
          >
            {templateSaving ? t('devices.modalEdit.saveTemplateLoading') : t('devices.modalEdit.saveTemplate')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t('devices.modalEdit.timezoneLabel')}</label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="input w-full"
          >
            <option value="">{t('devices.modalEdit.timezoneNone')}</option>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500">{t('devices.modalEdit.timezoneHelp')}</p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={timezoneSaving || (timezone || '') === currentTimezone}
            onClick={async () => {
              if (!device?.id) return
              setTimezoneSaving(true)
              try {
                const updated = await updateDevice.mutateAsync({
                  id: device.id,
                  payload: { timezone: timezone || '' },
                })
                onDevicePatch?.(updated)
                await onAfterWebDeviceChange?.()
              } catch (err) {
                alert(err.message || t('devices.modalEdit.saveTimezoneError'))
              } finally {
                setTimezoneSaving(false)
              }
            }}
          >
            {timezoneSaving ? t('devices.modalEdit.saveTimezoneLoading') : t('devices.modalEdit.saveTimezone')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-800">{t('devices.modalEdit.remoteSection')}</h4>
          {!wdOn ? (
            <button
              type="button"
              className="btn btn-outline w-full justify-center"
              disabled={enableWd.isPending || wdDisabled}
              title={wdDisabled ? t('devices.modalEdit.noDevEui') : undefined}
              onClick={runEnable}
            >
              {enableWd.isPending ? t('devices.modalEdit.enableLoading') : t('devices.modalEdit.enableRemote')}
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="btn btn-outline w-full justify-center"
                disabled={regenWd.isPending || wdDisabled}
                onClick={runRegen}
              >
                {regenWd.isPending ? t('devices.modalEdit.regenLoading') : t('devices.modalEdit.regenToken')}
              </button>
              <button
                type="button"
                className="btn btn-outline w-full justify-center border-amber-300 text-amber-900 hover:bg-amber-50"
                disabled={disableWd.isPending || wdDisabled}
                onClick={runDisable}
              >
                {disableWd.isPending ? t('devices.modalEdit.disableLoading') : t('devices.modalEdit.disableRemote')}
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t('devices.modalEdit.alarmRecipientsLabel')}</label>
          <p className="text-xs text-gray-500">{t('devices.modalEdit.alarmRecipientsHint')}</p>
          {(alarmCandidates ?? []).length === 0 ? (
            <p className="text-xs text-amber-700">{t('devices.modalEdit.alarmRecipientsEmpty')}</p>
          ) : (
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {(alarmCandidates ?? []).map((c) => {
                const tuid = c.tenantUserId ?? c.TenantUserId
                const checked = recipientIds.has(tuid)
                return (
                  <label key={tuid} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setRecipientIds((prev) => {
                          const next = new Set(prev)
                          if (next.has(tuid)) next.delete(tuid)
                          else next.add(tuid)
                          return next
                        })
                      }
                    />
                    <div className="min-w-0">
                      <div className="text-sm text-gray-800 truncate">{c.name ?? c.Name}</div>
                      <div className="text-xs text-gray-500 truncate">{c.phoneNumber ?? c.PhoneNumber}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={recipientsSaving || !device?.id}
            onClick={async () => {
              if (!device?.id) return
              setRecipientsSaving(true)
              try {
                await setAlarmRecipients.mutateAsync(Array.from(recipientIds))
              } catch (err) {
                alert(err.message || t('devices.modalEdit.alarmRecipientsError'))
              } finally {
                setRecipientsSaving(false)
              }
            }}
          >
            {recipientsSaving ? t('devices.modalEdit.alarmRecipientsSaving') : t('devices.modalEdit.alarmRecipientsSave')}
          </button>
        </div>

        <div className="flex justify-end pt-2">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            {t('devices.modalEdit.close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
